import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { Download, Loader2, Pause, Play } from 'lucide-react'
import { canPlayType, formatDuration } from '@/lib/voiceRecording'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'

/**
 * A spoken message, playable where it sits.
 *
 * Used both for a recording that has not been sent yet and for one read back off
 * a submission, which is why the source arrives as a function: the first is an
 * object URL that already exists, the second is a signed link that has to be
 * asked for and expires. Neither belongs in the page until somebody presses
 * play.
 *
 * The length is given rather than read from the element. An `<audio>` element
 * reports `Infinity` for a MediaRecorder's own WebM output — see
 * lib/voiceRecording.ts — so a player that trusted it would show a scrub bar
 * that never fills.
 */
export function VoiceMessage({
  label,
  durationSec,
  mimeType,
  source,
  actions,
}: {
  label: string
  /** Seconds, as counted by whoever recorded it. Null when never captured. */
  durationSec: number | null
  /** What the server detected. Used to warn before a silent failure. */
  mimeType?: string | null
  /**
   * Where the audio comes from, in whichever form the caller already has.
   *
   * A single prop rather than "pass a string or else a loader", because the
   * ambiguity had a bug in it: the recorder used to hand over a loader that
   * read a URL its own effect had not created yet. React runs child effects
   * *before* parent effects, so the prefetch below fired first, the loader
   * threw, and the player showed "cannot load" for a recording sitting in
   * memory. A Blob is now handled here, where its lifetime is owned by one
   * component.
   */
  source: string | Blob | (() => Promise<string>)
  actions?: ReactNode
}) {
  const t = useT()
  const audioRef = useRef<HTMLAudioElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const [src, setSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [position, setPosition] = useState(0)
  const [failed, setFailed] = useState(false)

  /*
   * Asked before anything is fetched.
   *
   * An iPhone handed a WebM reports readiness and then plays silence — no
   * error, no event. Without this the worker presses play, nothing happens, and
   * the reasonable conclusion is that the app is broken. See canPlayType.
   */
  const playable = canPlayType(mimeType)

  /*
   * The source is fetched on mount rather than on the first tap, and that is an
   * iOS requirement rather than an optimisation.
   *
   * Safari on iOS only honours `play()` when it is called **synchronously inside
   * the handler for a user gesture**. The original version tapped, awaited a
   * signed URL, let React render the `<audio>`, and called `play()` from
   * `onCanPlay` — several turns of the event loop away from the tap. iOS
   * refuses that and, worse, refuses it *silently*: no error, no event, just a
   * play button that does nothing while the same code works on every desktop.
   *
   * Having the URL in hand before the tap means the handler can call `play()`
   * with no await in front of it. The cost is one signed request per voice
   * message on a page that has any, which is a fair price for a play button
   * that works on half the phones in the plant.
   */
  /*
   * The loader is held in a ref and the effect keyed on everything *except* it.
   *
   * Callers pass `source={() => fetchAttachmentUrl(id)}` inline, which is a new
   * function on every render. Depending on it directly would re-run this effect
   * every render — an endless stream of signed-URL requests from a page that
   * looks idle. A string or a Blob is a value and can be depended on normally.
   */
  const loaderRef = useRef(source)
  loaderRef.current = source

  const staticSource = typeof source === "function" ? null : source

  useEffect(() => {
    const source = staticSource ?? loaderRef.current

    if (typeof source === "string") {
      setSrc(source)
      return
    }

    if (source instanceof Blob) {
      // Owned here, revoked here. An object URL pins the whole blob in memory
      // until it is released, and these are recordings held on a phone.
      const objectUrl = URL.createObjectURL(source)
      setSrc(objectUrl)

      return () => URL.revokeObjectURL(objectUrl)
    }

    let cancelled = false
    setLoading(true)

    ;(source as () => Promise<string>)()
      .then((url) => {
        if (!cancelled) setSrc(url)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [staticSource])

  /** Synchronous on purpose — see the effect above. No `await` may appear here. */
  function toggle() {
    const audio = audioRef.current
    if (!audio) return

    if (audio.paused) {
      // The rejection is swallowed: iOS rejects when it decides the gesture did
      // not count, and there is nothing useful to say beyond the button not
      // having worked.
      void audio.play().catch(() => {})
    } else {
      audio.pause()
    }
  }

  /**
   * Scrub by pressing anywhere on the bar, and by dragging along it.
   *
   * Pointer events rather than mouse, so a thumb on a phone works the same as a
   * mouse — which is the case that matters here. Pointer capture keeps the drag
   * alive when the finger leaves the bar, so a scrub that wanders vertically
   * does not stop dead halfway.
   */
  function seekTo(event: ReactPointerEvent<HTMLDivElement>) {
    if (!playable || !durationSec) return

    const bar = barRef.current
    if (!bar) return

    const { left, width } = bar.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (event.clientX - left) / width))
    const seconds = ratio * durationSec

    setPosition(seconds)

    const audio = audioRef.current
    if (audio) audio.currentTime = seconds
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId)
    seekTo(event)
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    // buttons is a bitmask; 1 means the primary button or the finger is down.
    if (event.buttons === 1) seekTo(event)
  }

  const progress = durationSec && durationSec > 0 ? Math.min(1, position / durationSec) : 0

  return (
    <div className="flex items-center gap-3 rounded-xl bg-[var(--surface-sunken)] px-3 py-2.5">
      <button
        type="button"
        onClick={playable ? toggle : undefined}
        disabled={loading || !playable}
        // The caller's own label is interpolated rather than glued on: the word
        // order of "play X" is not the same in every language on the switcher.
        aria-label={t(playing ? 'submissions.voice.pause' : 'submissions.voice.play', { label })}
        className="bg-brand-600 flex size-11 shrink-0 items-center justify-center rounded-full text-white disabled:opacity-60"
      >
        {loading ? (
          <Loader2 className="size-5 animate-spin" aria-hidden />
        ) : playing ? (
          <Pause className="size-5 fill-current" aria-hidden />
        ) : (
          <Play className="size-5 translate-x-px fill-current" aria-hidden />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{label}</p>

        {!playable ? (
          <p className="muted mt-0.5 text-xs">{t('submissions.voice.unsupported')}</p>
        ) : failed ? (
          <p role="alert" className="mt-0.5 text-xs font-medium text-rose-600">
            {t('submissions.voice.loadFailed')}
          </p>
        ) : (
          /*
           * A slider, not decoration. Somebody re-listening to catch a machine
           * name should not have to restart a two-minute message to hear ten
           * seconds of it again.
           */
          <div
            ref={barRef}
            role="slider"
            tabIndex={0}
            aria-label={t('submissions.voice.position', { label })}
            aria-valuemin={0}
            aria-valuemax={durationSec ?? 0}
            aria-valuenow={Math.round(position)}
            aria-valuetext={formatDuration(position)}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onKeyDown={(e) => {
              if (!durationSec) return
              const step = e.key === 'ArrowRight' ? 5 : e.key === 'ArrowLeft' ? -5 : 0
              if (!step) return

              e.preventDefault()
              const next = Math.min(durationSec, Math.max(0, position + step))
              setPosition(next)
              if (audioRef.current) audioRef.current.currentTime = next
            }}
            className={cn(
              'group relative mt-1.5 flex h-4 items-center',
              durationSec ? 'cursor-pointer' : 'cursor-default',
            )}
          >
            <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
              <div
                className="bg-brand-600 h-full rounded-full"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>

            {/* The handle appears on hover and while playing — a static dot on
                a bar nobody can drag is a promise the control does not keep. */}
            {durationSec ? (
              <span
                aria-hidden
                className={cn(
                  'bg-brand-600 pointer-events-none absolute size-3 -translate-x-1/2 rounded-full',
                  'ring-2 ring-[var(--surface-sunken)] transition-opacity',
                  playing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                )}
                style={{ left: `${Math.round(progress * 100)}%` }}
              />
            ) : null}
          </div>
        )}
      </div>

      <span className="muted shrink-0 text-xs tabular-nums">
        {position > 0 ? formatDuration(position) : formatDuration(durationSec ?? 0)}
      </span>

      {!playable && (
        <button
          type="button"
          onClick={() => src && window.open(src, '_blank', 'noopener,noreferrer')}
          aria-label={t('submissions.voice.download', { label })}
          className="muted shrink-0"
        >
          <Download className="size-4" aria-hidden />
        </button>
      )}

      {actions}

      {/* Always rendered, never conditional: toggle() must find an element to
          call play() on inside the gesture, and a conditional one is not there
          yet on the first tap. */}
      {(
        <audio
          ref={audioRef}
          src={src ?? undefined}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
          onError={() => setFailed(true)}
          onEnded={() => {
            setPlaying(false)
            setPosition(0)
          }}
        />
      )}
    </div>
  )
}
